import EventDelegation from '@jjwesterkamp/event-delegation'
import { hide, queryAll, queryOne, removeClasses, show, showIf, toggleClasses, touchAll } from 'lambda-dom'
import { construct, find, last, prop } from 'ramda'

import { isString } from '@app-helpers'
import { current, push, replace } from '@app-services'
import { SpeakUpStep } from './speak-up-step'
import type { StepLinkElement, TranslateToggle } from './speak-up.types'
import { ModalLinkElement, SpeakUpSection } from './speak-up.types'
import {
    deactivateElementOnBodyClick,
    getSpeakUpUrlForLanguage,
    translate,
} from './speak-up.lib'
import { SpeakUpModal } from './speak-up-modal'

const historyQueryKey = 'h'

// @ts-ignore
const stepPool: SpeakUpStep[] = queryAll('[data-speakup-step]').map(construct(SpeakUpStep))
// @ts-ignore
const modalPool: SpeakUpModal[] = queryAll('[data-speakup-modal]').map(construct(SpeakUpModal))

const sections = {
    [SpeakUpSection.Intro]: queryOne<HTMLElement>(`[data-speakup-section="${SpeakUpSection.Intro}"]`)!,
    [SpeakUpSection.Questionnaire]: queryOne<HTMLElement>(`[data-speakup-section="${SpeakUpSection.Questionnaire}"]`)!,
    [SpeakUpSection.Phone]: queryOne<HTMLElement>(`[data-speakup-section="${SpeakUpSection.Phone}"]`)!,
}

// ------------------------------------------------------------------------------
//      Init
// ------------------------------------------------------------------------------

const stepHistoryStack: SpeakUpStep[] = queryValueToStack(stepPool)

if (stepHistoryStack.length === 0) {
    const activeStep = findActiveStep()!
    stepHistoryStack.push(activeStep)
    replaceBrowserHistory()
    displayStepElement(activeStep.reference())
} else {
    displayStepElement(last(stepHistoryStack)!.reference())
    proceedToQuestionnaire()
}

// ------------------------------------------------------------------------------
//      Events
// ------------------------------------------------------------------------------

window.addEventListener('popstate', () => {
    stepHistoryStack.pop()
    const lastStep = last(stepHistoryStack)
    if (lastStep) {
        displayStepElement(lastStep.reference())
    }
})

EventDelegation
    .global()
    .events('click')
    .select<StepLinkElement>('[data-speakup-target]')
    .listen((e) => {
        const targetStep = getStepByReference(e.delegator.dataset.speakupTarget)
        if (targetStep) {
            appendStep(targetStep)
        }
    })

EventDelegation
    .global()
    .events('click')
    .select<ModalLinkElement>('[data-speakup-modal-target]')
    .listen((e) => {
        const targetModal = getModalByReference(e.delegator.dataset.speakupModalTarget)
        if (targetModal) {
            e.preventDefault()
            targetModal.show()
        }
    })

EventDelegation
    .global()
    .events('click')
    .select('[data-speakup-back]')
    .listen((e) => {
        e.preventDefault()
        window.history.back()
    })

EventDelegation
    .global()
    .events('click')
    .select('[data-speakup-start]')
    .listen((e) => {
        e.preventDefault()
        proceedToQuestionnaire()
    })

EventDelegation
    .global()
    .events('click')
    .select('[data-speakup-proceed]')
    .listen(proceedToForm)

EventDelegation
    .global()
    .events('click')
    .select('[data-speakup-phone]')
    .listen(proceedToPhone)

EventDelegation
    .global()
    .events('click')
    .select('[data-speakup-phone-back]')
    .listen(proceedToQuestionnaire)

touchAll([
    'input#involvement-toggle-checkbox',
    'div#involvement-input-row',
], (inputElement, inputRow) => {
    inputElement.addEventListener('change', () => showIf(inputElement.checked)(inputRow))
})

touchAll([
    '.speak-up--locale--selected',
    '.speak-up--locale--dropdown',
], (toggle, dropdown) => {
    toggle.addEventListener('click', () => toggleClasses('active')(dropdown))

    EventDelegation
        .within(dropdown)
        .events('click')
        .select<TranslateToggle>('[data-speakup-localize]')
        .listen((e) => {
            removeClasses('active')(dropdown)

            const lang = e.delegator.dataset.speakupLocalize
            translate(lang)
        })

    deactivateElementOnBodyClick(dropdown, '.speak-up--locale')
})

EventDelegation
    .global()
    .events('click')
    .select<HTMLButtonElement>('[data-bs-dismiss="modal"]')
    .listen(() => {
        const modals = document.querySelectorAll('[data-speakup-modal]')
        modals.forEach((modal) => removeClasses('active', 'show')(modal))
    })

// ------------------------------------------------------------------------------
//      Utils
// ------------------------------------------------------------------------------

function proceedToQuestionnaire(): void {
    hide(sections.intro)
    show(sections.questionnaire)
    hide(sections.phone)
    queryOne('.speak-up')?.classList.add('started')
}

function proceedToForm(): void {
    const language = current.queryObject().l ?? 'en'

    const url = getSpeakUpUrlForLanguage(language.toString())

    window.open(url, '_blank')
}

function proceedToPhone(): void {
    hide(sections.intro)
    hide(sections.questionnaire)
    show(sections.phone)
}

function appendStep(step: SpeakUpStep): void {
    stepHistoryStack.push(step)
    updateBrowserHistory()
    displayStepElement(step.reference())
}

function findActiveStep(): SpeakUpStep | null {
    return find((step) => step.isActive(), stepPool) ?? null
}

function displayStepElement(reference: string) {
    for (const step of stepPool) {
        if (step.reference() === reference) {
            step.show()
        } else {
            step.hide()
        }
    }
}

function getStepByReference(reference: string, pool: SpeakUpStep[] = stepPool) {
    return find((step) => step.reference() === reference, pool)
}

function getModalByReference(reference: string, pool: SpeakUpModal[] = modalPool) {
    return find((modal) => modal.reference() === reference, pool)
}

function updateBrowserHistory() {
    push.patchQueryParams({
        [historyQueryKey]: stackToQueryValue(),
    }, true, { encode: false })
}

function replaceBrowserHistory() {
    replace.patchQueryParams({
        [historyQueryKey]: stackToQueryValue(),
    }, true, { encode: false })
}

function stackToQueryValue(): string {
    return stepHistoryStack
        .map((step) => step.reference())
        .join(',')
}

function queryValueToStack(pool: SpeakUpStep[]): SpeakUpStep[] {
    const csv = prop(historyQueryKey, current.queryObject())

    return isString(csv)
        ? csv.split(',').map((reference) => getStepByReference(reference, pool)!)
        : []
}

document.addEventListener('DOMContentLoaded', () => {
    const urlParams = new URLSearchParams(window.location.search)

    const language = urlParams.get('l')

    if (language) translate(language)
})
